/**
 * Relay-held Polymarket account state for the feed plane (feed-filters-
 * per-view, Danielle round 1).
 *
 * Polymarket HELD POSITIONS exist only through `/api/polymarket/positions`
 * (`usePolymarketRelayPositions`) and the org-key RESTING ORDERS only through
 * `/api/polymarket/orders` (`usePolymarketRelayOrders`); `usePolyAccountStore`
 * carries the personal-key CLOB orders alone. Until now those relay hooks
 * were mounted only by position-aware views, so with none mounted the account
 * plane could not name a relay-held Poly identity — and a never-seen held
 * player-prop stayed excluded from the edge scope AND the class-trimmed core
 * floor indefinitely. `AccountFeedNeedsBridge` mounts both hooks once on
 * every authenticated route and mirrors their AUTHORITATIVE results here;
 * `accountFeedNeeds` reads this store beside the two account stores.
 *
 * What "authoritative" means, mirrored from the hooks' own semantics:
 *  - only a `ready` result REPLACES the rows (the hooks already debounce a
 *    transient empty from visible rows, and hold rows through a transient
 *    coded 404);
 *  - `loading` / `error` / `unavailable` / `idle` never write — the last
 *    known rows stand (a blind read is not "no position");
 *  - the rows are stamped with the authenticated identity (user:org) they
 *    were fetched under; a different identity RESETS the store before
 *    anything is applied, so the previous account's rows can never be
 *    declared under the next (no cross-account leakage).
 */
import { create } from 'zustand';
import type { PolyRelayOpenOrder, PolyRelayPosition } from '@/lib/polymarket/relay';

export type RelayReadStatus = 'idle' | 'loading' | 'ready' | 'error' | 'unavailable';

export interface PolyRelayAccountState {
  /** `user:org` the rows were fetched under; '' before any identity. */
  identity: string;
  positions: PolyRelayPosition[];
  orders: PolyRelayOpenOrder[];
  /** A `ready` result has been applied under this identity at least once. */
  positionsKnown: boolean;
  ordersKnown: boolean;
  applyPositions: (identity: string, status: RelayReadStatus, positions: readonly PolyRelayPosition[]) => void;
  applyOrders: (identity: string, status: RelayReadStatus, orders: readonly PolyRelayOpenOrder[]) => void;
  reset: () => void;
}

const EMPTY = { positions: [] as PolyRelayPosition[], orders: [] as PolyRelayOpenOrder[], positionsKnown: false, ordersKnown: false };

export const usePolyRelayAccountStore = create<PolyRelayAccountState>((set, get) => ({
  identity: '',
  ...EMPTY,
  applyPositions: (identity, status, positions) => {
    const base = get().identity === identity ? get() : { ...EMPTY, identity };
    if (status !== 'ready') {
      if (base !== get()) set({ identity, ...EMPTY });
      return;
    }
    set({ identity, positions: [...positions], positionsKnown: true, orders: base.orders, ordersKnown: base.ordersKnown });
  },
  applyOrders: (identity, status, orders) => {
    const base = get().identity === identity ? get() : { ...EMPTY, identity };
    if (status !== 'ready') {
      if (base !== get()) set({ identity, ...EMPTY });
      return;
    }
    set({ identity, orders: [...orders], ordersKnown: true, positions: base.positions, positionsKnown: base.positionsKnown });
  },
  reset: () => set({ identity: '', ...EMPTY }),
}));
